import LayoutHR from "layouts/layout-hr";
import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import { deleteRequest } from "services/api";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import {
  AttendanceSettingProvider,
  useAttendanceSetting,
} from "./context/AttendanceSettingContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";

function AttendanceSettingContent() {
  const {
    attendanceSettings,
    filterOptions,
    handleSearch,
    refetch,
    Pagination,
    loadOfficeOptions,
    loadUserOptions,
  } = useAttendanceSetting();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  const { handleError, handleSuccessToast } = useCanvasHandler();

  // Load default options on component mount
  useEffect(() => {
    const loadDefaultOptions = async () => {
      try {
        const [offices, users] = await Promise.all([
          loadOfficeOptions(),
          loadUserOptions(),
        ]);
        setOfficeOptions(offices);
        setUserOptions(users);
      } catch (error) {
        console.error("Error loading default options:", error);
      }
    };

    loadDefaultOptions();
  }, [loadOfficeOptions, loadUserOptions]);

  // Handle create new attendance setting
  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  // Handle edit attendance setting
  const handleEdit = (item) => {
    // Transform select fields to objects for proper display
    const transformedItem = { ...item };
    if (officeOptions.length > 0 && item.office_id) {
      const office = officeOptions.find((o) => o.value === item.office_id);
      if (office) {
        transformedItem.office_id = office;
      }
    }
    if (userOptions.length > 0 && item.user_id) {
      const user = userOptions.find((u) => u.value === item.user_id);
      if (user) {
        transformedItem.user_id = user;
      }
    }
    setEditingItem(transformedItem);
    setShowModal(true);
  };

  // Handle delete attendance setting
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/absensi-settings/${id}`);

        if (response.success) {
          handleSuccessToast(true, "Attendance setting deleted successfully");
          refetch(); // Refresh the data
        } else {
          Swal.fire({
            title: "Error",
            text: response.message || "Failed to delete attendance setting",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#dc3545"
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete attendance setting. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545"
        });
      }
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(
      true,
      "Attendance setting saved successfully (Demo Mode)"
    );
  };

  // Handle form error
  const handleFormError = (error) => {
    handleError(error);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Form fields configuration
  const formFields = [
    {
      name: "office_id",
      label: "Office",
      type: "select",
      required: true,
      loadOptions: loadOfficeOptions,
      defaultOptions: true,
      placeholder: "Select office",
    },
    {
      name: "user_id",
      label: "User",
      type: "select",
      required: true,
      loadOptions: loadUserOptions,
      defaultOptions: true,
      placeholder: "Select user",
    },
    {
      name: "is_active",
      label: "Auto Generate Attendance",
      type: "checkbox",
      required: false,
    },
    {
      name: "auto_generate",
      label: "Schedule Time (H:i)",
      type: "time",
      required: false,
      placeholder: "e.g. 01:00",
    },
    {
      name: "timezone",
      label: "Timezone",
      type: "select",
      required: false,
      options: [
        { value: "Asia/Makassar", label: "Asia/Makassar (WITA)" },
        { value: "Asia/Jakarta", label: "Asia/Jakarta (WIB)" },
        { value: "Asia/Jayapura", label: "Asia/Jayapura (WIT)" },
      ],
    },
    {
      name: "max_late",
      label: "Max Late (Minutes)",
      type: "number",
      required: false,
      placeholder: "e.g. 15",
    },
    {
      name: "is_on_site",
      label: "On Site",
      type: "checkbox",
    },
    {
      name: "max_late",
      label: "Max Late (minutes)",
      type: "number",
      required: true,
      placeholder: "Enter max late minutes",
    },
    {
      name: "ip_whitelist",
      label: "IP Whitelist",
      type: "text",
      required: true,
      placeholder: "Enter IP whitelist (e.g., 192.168.1.0/24)",
    },
    {
      name: "all_workdays",
      label: "Select All Workdays",
      type: "checkbox",
    },
    {
      name: "monday",
      label: "Monday",
      type: "checkbox",
    },
    {
      name: "tuesday",
      label: "Tuesday",
      type: "checkbox",
    },
    {
      name: "wednesday",
      label: "Wednesday",
      type: "checkbox",
    },
    {
      name: "thursday",
      label: "Thursday",
      type: "checkbox",
    },
    {
      name: "friday",
      label: "Friday",
      type: "checkbox",
    },
    {
      name: "saturday",
      label: "Saturday",
      type: "checkbox",
    },
    {
      name: "sunday",
      label: "Sunday",
      type: "checkbox",
    },
    {
      name: "office_hour_start_monday",
      label: "Office Start Time (Monday)",
      type: "time",
      required: true,
      defaultValue: "08:00",
    },
    {
      name: "office_hour_end_monday",
      label: "Office End Time (Monday)",
      type: "time",
      required: true,
      defaultValue: "17:00",
    },
    {
      name: "bonus_early",
      label: "Bonus Early (IDR)",
      type: "number",
      placeholder: "Enter bonus for early arrival",
      isPrice: true,
    },
    {
      name: "bonus_early_minute",
      label: "Bonus Early Minutes",
      type: "number",
      placeholder: "Enter minutes for early bonus",
      isPrice: true,
    },
    {
      name: "bonus_late",
      label: "Bonus Late (IDR)",
      type: "number",
      placeholder: "Enter bonus for late arrival",
      isPrice: true,
    },
    {
      name: "bonus_late_minute",
      label: "Bonus Late Minutes",
      type: "number",
      placeholder: "Enter minutes for late bonus",
      isPrice: true,
    },
  ];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Attendance Settings</h2>
              <div className="text-muted mt-1">
                Manage attendance settings for offices and users
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Setting
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search attendance settings..."
                    value={filterOptions.search}
                    onChange={(e) => {
                      // Update search value in filterOptions
                      // const newSearch = e.target.value;
                      // Note: Actual search is handled on Enter key press
                    }}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Office</th>
                    <th>User</th>
                    <th>On Site</th>
                    <th>Max Late</th>
                    <th>IP Whitelist</th>
                    <th>Work Days</th>
                    <th>Office Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceSettings.loading ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading attendance settings...
                      </td>
                    </tr>
                  ) : attendanceSettings.data?.length > 0 ? (
                    attendanceSettings.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.office?.name || item.office_id}</td>
                        <td>{item.user?.name || item.user_id}</td>
                        <td>
                          <span
                            className={`badge text-white ${
                              item.is_on_site ? "bg-success" : "bg-secondary"
                            }`}
                          >
                            {item.is_on_site ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>{item.max_late} min</td>
                        <td>
                          <code>{item.ip_whitelist}</code>
                        </td>
                        <td>
                          <div className="d-flex text-white flex-wrap gap-1">
                            {item.monday && (
                              <small className="badge text-white bg-primary">
                                Mon
                              </small>
                            )}
                            {item.tuesday && (
                              <small className="badge text-white bg-primary">
                                Tue
                              </small>
                            )}
                            {item.wednesday && (
                              <small className="badge text-white bg-primary">
                                Wed
                              </small>
                            )}
                            {item.thursday && (
                              <small className="badge text-white bg-primary">
                                Thu
                              </small>
                            )}
                            {item.friday && (
                              <small className="badge text-white bg-primary">
                                Fri
                              </small>
                            )}
                            {item.saturday && (
                              <small className="badge text-white bg-primary">
                                Sat
                              </small>
                            )}
                            {item.sunday && (
                              <small className="badge text-white bg-primary">
                                Sun
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          {item.office_hour_start_monday} -{" "}
                          {item.office_hour_end_monday}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No attendance settings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {attendanceSettings.pagination && <Pagination />}
          </div>
        </div>

        {/* Modal for Create/Edit */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem
                      ? "Edit Attendance Setting"
                      : "Create Attendance Setting"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/absensi-settings"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function AttendanceSettingPage() {
  return (
    <AttendanceSettingProvider>
      <AttendanceSettingContent />
    </AttendanceSettingProvider>
  );
}
